import { Card, Skeleton } from "@mui/material";
import React, { useMemo, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import SWR_KEYS from "@/constants/swr-keys";
import useToast from "@/contexts/toast-context";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useCreateServiceHours from "@/hooks/requests/service-hours/use-create-service-hours";
import useGetServiceHours from "@/hooks/requests/service-hours/use-get-service-hours";
import useRemoveServiceHours from "@/hooks/requests/service-hours/use-remove-service-hours";
import useUpdateServiceHours from "@/hooks/requests/service-hours/use-update-service-hours";
import useDialog from "@/hooks/use-dialog";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import usePagination from "@/hooks/use-pagination";
import { IServiceHours } from "@/interfaces/service-hours.interface";
import { IServiceHoursInput } from "@/interfaces/service-hours.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";
import serviceHoursService from "@/services/service-hours.service";

import ServiceHoursBulkDialog from "./ServiceHoursBulkDialog";
import ServiceHoursDialog from "./ServiceHoursDialog";
import ServiceHoursTable from "./ServiceHoursTable";
import { sortServiceHoursByWeekDay } from "../../constants";

type Props = {
  messages: ConfigurationTabMessages;
};

function ServiceHoursCard({ messages }: Props) {
  const { isVisible, handleClose, handleOpen, selected } = useDialog<IServiceHours>();
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const { data, isLoading, error } = useGetServiceHours({ pageNumber, pageSize });
  const { create, isLoading: isCreating } = useCreateServiceHours();
  const { update, isLoading: isUpdating } = useUpdateServiceHours();
  const { remove, isLoading: isRemoving } = useRemoveServiceHours();
  const { mutate } = useMutate();
  const { showToast } = useToast();
  const {
    isLoading: isBulkCreating,
    startLoading: startBulkLoading,
    endLoading: endBulkLoading,
  } = useLoading();

  const onSuccess = () => {
    mutate(SWR_KEYS.getServiceHours);
    handleClose();
  };

  const onBulkSuccess = () => {
    mutate(SWR_KEYS.getServiceHours);
    setIsBulkOpen(false);
  };

  const onSubmit = async (formData: IServiceHoursInput) => {
    if (selected) {
      await update({
        _id: selected.id,
        formData,
        successMessage: messages.serviceHoursUpdateSuccess,
        errorMessage: messages.serviceHoursUpdateError,
        onSuccess,
      });
      return;
    }

    await create({
      formData,
      successMessage: messages.serviceHoursCreateSuccess,
      errorMessage: messages.serviceHoursCreateError,
      onSuccess,
    });
  };

  const onBulkSubmit = async (items: IServiceHoursInput[]) => {
    startBulkLoading();

    try {
      for (const formData of items) {
        await serviceHoursService.create(formData);
      }

      showToast({ text: messages.serviceHoursBulkCreateSuccess, type: "success" });
      onBulkSuccess();
    } catch (error) {
      showToast({
        text: buildErrorMessage(error, messages.serviceHoursBulkCreateError),
        type: "error",
      });
    } finally {
      endBulkLoading();
    }
  };

  const onRemove = async (item: IServiceHours) => {
    await remove({
      _id: item.id,
      successMessage: messages.serviceHoursDeleteSuccess,
      errorMessage: messages.serviceHoursDeleteError,
      onSuccess: () => mutate(SWR_KEYS.getServiceHours),
    });
  };

  const isEmpty = (data?.totalCount ?? 0) === 0;
  const sortedItems = useMemo(
    () => sortServiceHoursByWeekDay(data?.items ?? []),
    [data?.items]
  );

  const onOpenCreate = () => {
    if (isEmpty) {
      setIsBulkOpen(true);
      return;
    }

    handleOpen();
  };

  const isSaving = isCreating || isUpdating || isRemoving || isBulkCreating;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDTypography variant="h5" mb={2}>
          {messages.serviceHoursSection}
        </MDTypography>

        {isLoading && <Skeleton variant="rectangular" width="100%" height={120} />}

        {error && !isLoading && (
          <AlertBlock
            title={messages.serviceHoursLoadErrorTitle}
            description={messages.serviceHoursLoadErrorDescription}
            alt={messages.serviceHoursLoadErrorTitle}
          />
        )}

        {!isLoading && !error && data && (
          <ServiceHoursTable
            items={sortedItems}
            messages={messages}
            data={data}
            pagination={pagination}
            onRemove={onRemove}
            onEdit={handleOpen}
            onOpenCreate={onOpenCreate}
            isEmpty={isEmpty}
          />
        )}
      </MDBox>

      <ServiceHoursDialog
        isOpen={isVisible}
        onClose={handleClose}
        messages={messages}
        isLoading={isSaving}
        selected={selected}
        onSubmit={onSubmit}
      />

      <ServiceHoursBulkDialog
        isOpen={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        messages={messages}
        isLoading={isBulkCreating}
        onSubmit={onBulkSubmit}
      />
    </Card>
  );
}

export default ServiceHoursCard;
